/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "./Log";
import { Sage } from "./Sage";
import { PendingTelemetryStore } from "./PendingTelemetryStore";
import { SageIdentityGate } from "./SageIdentityGate";
import type {
  LogDetails,
  TAnalyticsFields,
  TCarouselItem,
  TCarouselRail,
  TContentType
} from "../types";

const TAG = "AnalyticsLogger";
const EVENT_NAME_PLAYBACK_SESSION = "playbackSession";
const EVENT_NAME_VIEWED_SCREEN = "viewedScreen";
const EVENT_NAME_CONTENT_HIGHLIGHTED = "contentHighlighted";
const EVENT_NAME_CONTENT_SELECTED = "contentSelected";
const EVENT_NAME_VIEWED_CONTENT_DETAILS = "viewedContentDetails";
const EVENT_NAME_ITEM_SELECTED = "itemSelected";
const EVENT_NAME_SEARCH_QUERY = "searchQuery";
const EVENT_NAME_CREATE_RECORDING = "createRecording";
const EVENT_NAME_MODIFY_RECORDING = "modifyRecording";
const EVENT_NAME_CANCEL_RECORDING = "cancelRecording";
const EVENT_NAME_DELETE_RECORDING = "deleteRecording";
const EVENT_NAME_CREATE_ONE_PASS = "createOnePass";
const EVENT_NAME_MODIFY_ONE_PASS = "modifyOnePass";
const EVENT_NAME_CANCEL_ONE_PASS = "cancelOnePass";
const EVENT_TYPE_IMPRESSION = "impression";
const EVENT_TYPE_CLICK = "click";
const EVENT_REASON_DWELL = "dwell";
const FEED_TYPE_CHANNEL = "CHANNEL_TYPE";
const FEED_TYPE_CONTENT = "CONTENT_TYPE";
const PROMOTION_FLAG_NONE = "N";

/**
 * Optional pgd/pgd_details fields for linear (broadcast) content — populated
 * for Guide's program cells in addition to the base carousel-tile fields
 * (content_type, channel_id, season_number, episode_number) every screen
 * already shares.
 */
const LINEAR_PGD_OPTIONAL_KEYS: string[] = [
  "channel_num",
  "channel_call_sign",
  "collection_id",
  "collection_title",
  "program_start_time",
  "content_duration",
  "year",
  "genre",
  "tile_id"
];

/**
 * Optional fields shared by the recording/OnePass mutation events
 * (createRecording, modifyRecording, cancelRecording, deleteRecording,
 * createOnePass, modifyOnePass, cancelOnePass) — copied through only when the
 * caller supplies them, per the spec's "omit empty/default fields" rule.
 */
const RECORDING_EVENT_OPTIONAL_KEYS: string[] = [
  "collection_id",
  "collection_title",
  "station_id",
  "channel_id",
  "provider_id",
  "channel_call_sign",
  "channel_num",
  "program_start_time",
  "content_duration",
  "content_type",
  "content_network",
  "season_number",
  "episode_number",
  "original_air_date",
  "year",
  "genre",
  "pgd_resolution",
  "recording_id",
  "series_recording",
  "is_soft_delete",
  "batch_id",
  "start_time",
  "padding_duration_before_start_option",
  "padding_duration_after_stop_option",
  "deletion_policy_option",
  "channels_option",
  "keep_at_most_option",
  "get_in_hd_option",
  "record_type_option",
  "include_option",
  "rent_or_buy_option",
  "start_from_option",
  "available_from",
  "previous_start_time_option",
  "previous_deletion_policy_option",
  "previous_padding_duration_before_start_option",
  "previous_padding_duration_after_stop_option",
  "previous_get_in_hd_option",
  "previous_include_option",
  "previous_keep_at_most_option",
  "previous_record_type_option",
  "previous_rent_or_buy_option",
  "previous_start_from_option",
  "screen",
  "reason"
];

/**
 * Screen-specific configuration passed to {@link AnalyticsLogger.create}.
 *
 * Define these as named constants in the screen's constants file and pass
 * them once at logger creation time rather than on every log call.
 */
export interface AnalyticsScreenConfig {
  screenId: string;
  screenName: string;
}

/**
 * Analytics logger backed by the {@link Sage} transport.
 *
 * Independent logger — not part of the `Log` console fan-out. Fire-and-forget:
 * Sage buffers internally and uploads asynchronously with retry.
 *
 * Hosts analytics telemetry helpers for screen and content interactions.
 * Screen-scoped methods use `this.SCREEN_ID` and `this.SCREEN_NAME`, which
 * are resolved via TypeScript static `this` polymorphism at call time.
 *
 * Usage:
 *   1. Call {@link AnalyticsLogger.create} with the screen's config.
 *   2. Call all logging methods through the returned class:
 *        MyScreenAnalyticsLogger.logContentHighlighted(...)
 *
 * Event names and field names match the Unity Client Logging Specification:
 *   Analytics: viewedScreen | contentHighlighted | contentSelected | viewedContentDetails
 * Events raised before Sage identity exists are queued and replayed once it
 * does — see {@link SageIdentityGate}.
 */
export class AnalyticsLogger {
  /**
   * Stable screen identifier used as screen_id across all events.
   * Override via {@link AnalyticsLogger.create} with the actual screen endpoint, e.g. "/screens/unityHome".
   */
  protected static readonly SCREEN_ID: string = "";

  /**
   * Human-readable screen label used as screen_name across analytics events.
   * Override via {@link AnalyticsLogger.create} with the display name, e.g. "Home".
   */
  protected static readonly SCREEN_NAME: string = "";

  /**
   * Factory — creates a screen-specific logger bound to the supplied config.
   *
   * Each screen defines its own constants and passes them here once:
   *   const logger = AnalyticsLogger.create({
   *     screenId:   HOMESCREEN_CAROUSEL_SCREEN_ID,
   *     screenName: HOMESCREEN_CAROUSEL_SCREEN_NAME,
   *   });
   *
   * All static methods on the returned class carry the correct screen context
   * without any per-call configuration.
   */
  public static create(config: AnalyticsScreenConfig): typeof AnalyticsLogger {
    return class extends AnalyticsLogger {
      protected static override readonly SCREEN_ID = config.screenId;
      protected static override readonly SCREEN_NAME = config.screenName;
    };
  }

  /**
   * Builds the shared content interaction fields for a carousel tile.
   * Returns null when the carousel or tile index no longer resolves.
   */
  public static buildTileAnalyticsFields(
    carousels: TCarouselRail[],
    carouselIndex: number,
    tileIndex: number
  ): Partial<TAnalyticsFields> | null {
    const carousel = carousels[carouselIndex];
    if (carousel === undefined) {
      return null;
    }
    const item = carousel.items[tileIndex];
    if (item === undefined) {
      return null;
    }
    return {
      list_name: carousel.title,
      list_position: String(carouselIndex),
      tile_position: String(tileIndex),
      content_id: item.id,
      content_title: item.title,
      content_type: this.mapContentTypeToDisplay(item.contentType),
      channel_id: item.stationId !== undefined ? item.stationId : "",
      season_number:
        item.seasonNumber !== undefined ? String(item.seasonNumber) : undefined,
      episode_number:
        item.episodeNumber !== undefined
          ? String(item.episodeNumber)
          : undefined,
      year: item.movieYear !== undefined ? String(item.movieYear) : undefined,
      feed_type: this.isChannelRail(carousel)
        ? FEED_TYPE_CHANNEL
        : FEED_TYPE_CONTENT,
      promotion_flag: PROMOTION_FLAG_NONE
    };
  }

  public static trackEvent(
    name: string,
    eventType?: string,
    details?: Partial<TAnalyticsFields>
  ): void {
    Log.d(TAG, `analytics event: ${name}`);
    // Blank eventType lets the transport apply its own default rather than duplicating it here.
    const analyticsEventType: string | undefined =
      eventType && eventType.trim() !== "" ? eventType : undefined;
    const normalizedDetails = AnalyticsLogger.normalizeDetails(details);

    if (SageIdentityGate.isReady()) {
      Sage.logAnalytics(name, analyticsEventType, normalizedDetails);
      return;
    }
    // Fire-and-forget — never block the caller
    PendingTelemetryStore.enqueueAnalytics(
      name,
      analyticsEventType,
      normalizedDetails
    ).catch(() => {
      // enqueue() already logs its own error; nothing else to do
    });
  }

  private static normalizeDetails(
    details?: Partial<TAnalyticsFields>
  ): LogDetails | undefined {
    if (details === undefined) {
      return undefined;
    }

    const normalized: LogDetails = {};
    Object.entries(details).forEach(([key, value]): void => {
      if (value !== undefined) {
        normalized[key] = value;
      }
    });
    return normalized;
  }

  private static isChannelRail(carousel: TCarouselRail): boolean {
    return (
      carousel.items.length > 0 &&
      carousel.items.every(
        (item: TCarouselItem): boolean =>
          item.contentType === "channel" ||
          item.contentType === "ottApplication"
      )
    );
  }

  private static mapContentTypeToDisplay(contentType: TContentType): string {
    switch (contentType) {
      case "channel":
      case "ottApplication":
        return "Channel";
      case "series":
        return "Series";
      case "movie":
        return "Movie";
      case "episode":
        return "Episode";
      case "sports":
      case "gameEvent":
      case "team":
      case "leagueOrganization":
        return "Sports";
      case "person":
        return "Person";
    }
  }

  private static getFieldOrEmpty(
    fields: Partial<TAnalyticsFields>,
    key: string
  ): string {
    const value = fields[key];
    return value !== undefined ? value : "";
  }

  private static copyOptionalFields(
    source: Partial<TAnalyticsFields>,
    target: TAnalyticsFields,
    keys: string[]
  ): void {
    keys.forEach((key: string): void => {
      const value = source[key];
      if (value !== undefined) {
        target[key] = String(value);
      }
    });
  }

  private static buildContentInteractionDetails(
    fields: Partial<TAnalyticsFields>,
    includePreviousScreenId: boolean,
    includeDwellTimeMs: boolean
  ): TAnalyticsFields {
    const listName: string = this.getFieldOrEmpty(fields, "list_name");
    const listPosition: string = this.getFieldOrEmpty(fields, "list_position");
    const tilePosition: string = this.getFieldOrEmpty(fields, "tile_position");
    const contentId: string = this.getFieldOrEmpty(fields, "content_id");
    const contentTitle: string = this.getFieldOrEmpty(fields, "content_title");

    const details: TAnalyticsFields = {
      screen_id: this.SCREEN_ID,
      screen_name: this.SCREEN_NAME,
      list_name: listName,
      list_position: listPosition,
      tile_position: tilePosition,
      content_id: contentId,
      content_title: contentTitle,
      scid: this.SCREEN_ID,
      scr: this.SCREEN_NAME,
      lst: listName,
      lpos: listPosition,
      tpos: tilePosition,
      cid: contentId,
      ct: contentTitle,
      timestamp: String(Date.now())
    };

    if (includePreviousScreenId) {
      const previousScreenId: string = this.getFieldOrEmpty(
        fields,
        "previous_screen_id"
      );
      details.previous_screen_id = previousScreenId;
      details.pscid = previousScreenId;
    }
    if (includeDwellTimeMs) {
      details.dwell_time_ms = this.getFieldOrEmpty(fields, "dwell_time_ms");
    }

    this.copyOptionalFields(fields, details, [
      "content_type",
      "channel_id",
      "season_number",
      "episode_number",
      "year",
      "feed_type",
      "promotion_flag",
      "rsn",
      ...LINEAR_PGD_OPTIONAL_KEYS
    ]);

    if (fields.content_type !== undefined) {
      details.cty = String(fields.content_type);
    }
    if (fields.channel_id !== undefined) {
      details.chid = String(fields.channel_id);
    }
    if (fields.season_number !== undefined) {
      details.sn = String(fields.season_number);
    }
    if (fields.episode_number !== undefined) {
      details.en = String(fields.episode_number);
    }
    if (fields.year !== undefined) {
      details.yr = String(fields.year);
    }
    if (fields.feed_type !== undefined) {
      details.ft = String(fields.feed_type);
    }
    if (fields.promotion_flag !== undefined) {
      details.pfi = String(fields.promotion_flag);
    }

    return details;
  }

  /** playbackSession analytics event emitted at session close. */
  public static logPlaybackSession(fields: Partial<TAnalyticsFields>): void {
    this.trackEvent(EVENT_NAME_PLAYBACK_SESSION, undefined, fields);
  }

  /** searchQuery analytics event emitted when a search is submitted. */
  public static logSearchQuery(fields: Partial<TAnalyticsFields>): void {
    this.trackEvent(EVENT_NAME_SEARCH_QUERY, undefined, {
      screen_id: this.getFieldOrEmpty(fields, "scid"),
      timestamp: String(Date.now()),
      q: this.getFieldOrEmpty(fields, "q"),
      qc: this.getFieldOrEmpty(fields, "qc"),
      qsrc: this.getFieldOrEmpty(fields, "qsrc"),
      scid: this.getFieldOrEmpty(fields, "scid"),
      scr: this.getFieldOrEmpty(fields, "scr"),
      pscid: this.getFieldOrEmpty(fields, "pscid")
    });
  }

  // ─────────────────────────── Analytics Events ────────────────────────────

  /**
   * viewedScreen — Emit when the screen first becomes visible and usable, or
   * (for screens that report time-on-screen instead) once on exit.
   *
   * Spec required: screen_id, screen_name, previous_screen_id.
   * Spec recommended: query_id, rail_count, time_on_screen (pass via `fields`).
   */
  public static logViewedScreen(fields: Partial<TAnalyticsFields>): void {
    const details: TAnalyticsFields = {
      screen_id: this.SCREEN_ID,
      screen_name: this.SCREEN_NAME,
      previous_screen_id: this.getFieldOrEmpty(fields, "previous_screen_id"),
      query_id: this.getFieldOrEmpty(fields, "query_id"),
      rail_count: this.getFieldOrEmpty(fields, "rail_count"),
      timestamp: String(Date.now())
    };
    this.copyOptionalFields(fields, details, ["time_on_screen"]);
    this.trackEvent(EVENT_NAME_VIEWED_SCREEN, EVENT_TYPE_IMPRESSION, details);
  }

  /**
   * contentHighlighted — Emit when a tile is focused long enough to qualify
   * as a browse highlight (after a dwell threshold has elapsed).
   *
   * Spec required: screen_id, list_name, list_position, tile_position,
   *                content_id, content_title.
   * Spec recommended: content_type, dwell_time_ms, channel_id, season_number,
   *                   episode_number (pass via `fields`).
   */
  public static logContentHighlighted(fields: Partial<TAnalyticsFields>): void {
    const details = this.buildContentInteractionDetails(fields, false, true);
    if (details.rsn === undefined || details.rsn.trim() === "") {
      details.rsn = EVENT_REASON_DWELL;
    }
    this.trackEvent(
      EVENT_NAME_CONTENT_HIGHLIGHTED,
      EVENT_TYPE_IMPRESSION,
      details
    );
  }

  /**
   * contentSelected — Emit when a user selects a carousel tile.
   *
   * Spec required: screen_id, list_name, list_position, tile_position,
   *                content_id, content_title.
   * Spec recommended: content_type, previous_screen_id, channel_id,
   *                   season_number, episode_number (pass via `fields`).
   */
  public static logContentSelected(fields: Partial<TAnalyticsFields>): void {
    const details = this.buildContentInteractionDetails(fields, true, false);
    this.trackEvent(EVENT_NAME_CONTENT_SELECTED, EVENT_TYPE_CLICK, details);
  }

  /**
   * viewedContentDetails — Emit when a tile selection navigates to a content
   * details page instead of immediately starting playback.
   *
   * Spec required: screen_id, previous_screen_id, content_id, content_title.
   * Spec recommended: source_screen_id, content_type,
   *                   list_name, list_position, tile_position (when available).
   */
  public static logViewedContentDetails(
    fields: Partial<TAnalyticsFields>
  ): void {
    const contentId: string =
      fields.content_id !== undefined ? fields.content_id : "";
    const contentTitle: string =
      fields.content_title !== undefined ? fields.content_title : "";

    const details: TAnalyticsFields = {
      screen_id: this.SCREEN_ID,
      previous_screen_id: this.SCREEN_ID,
      source_screen_id: this.SCREEN_ID,
      content_id: contentId,
      content_title: contentTitle,
      timestamp: String(Date.now())
    };
    if (fields.content_type !== undefined) {
      details.content_type = fields.content_type;
    }
    if (fields.list_name !== undefined) {
      details.list_name = fields.list_name;
    }
    if (fields.list_position !== undefined) {
      details.list_position = String(fields.list_position);
    }
    if (fields.tile_position !== undefined) {
      details.tile_position = String(fields.tile_position);
    }
    if (fields.source_screen_id !== undefined) {
      details.source_screen_id = fields.source_screen_id;
    }
    if (fields.previous_screen_id !== undefined) {
      details.previous_screen_id = fields.previous_screen_id;
    }
    this.copyOptionalFields(fields, details, [
      "channel_id",
      "season_number",
      "episode_number",
      ...LINEAR_PGD_OPTIONAL_KEYS
    ]);
    this.trackEvent(
      EVENT_NAME_VIEWED_CONTENT_DETAILS,
      EVENT_TYPE_IMPRESSION,
      details
    );
  }

  /**
   * itemSelected — Emit when the user selects a non-content UI element:
   * eg. a filter, or a channel/tile favourite toggle

   */
  public static logItemSelected(fields: Partial<TAnalyticsFields>): void {
    const details: TAnalyticsFields = {
      screen_id: this.SCREEN_ID,
      timestamp: String(Date.now())
    };
    this.copyOptionalFields(fields, details, [
      "list_name",
      "list_position",
      "tile_position",
      "tile_id",
      "reason",
      "previous_screen_id",
      "is_favorite"
    ]);
    this.trackEvent(EVENT_NAME_ITEM_SELECTED, EVENT_TYPE_CLICK, details);
  }

  /**
   * Shared field-building for the recording/OnePass mutation events below —
   * every one of them keys off a `content_id`/`content_title` and copies
   * through whichever recording/OnePass option fields the caller supplied.
   */
  private static buildRecordingEventDetails(
    fields: Partial<TAnalyticsFields>
  ): TAnalyticsFields {
    const details: TAnalyticsFields = {
      screen_id: this.SCREEN_ID,
      content_id: this.getFieldOrEmpty(fields, "content_id"),
      content_title: this.getFieldOrEmpty(fields, "content_title"),
      timestamp: String(Date.now())
    };
    this.copyOptionalFields(fields, details, RECORDING_EVENT_OPTIONAL_KEYS);
    return details;
  }

  /** createRecording — emit once a single (non-series) recording is scheduled. */
  public static logCreateRecording(fields: Partial<TAnalyticsFields>): void {
    this.trackEvent(
      EVENT_NAME_CREATE_RECORDING,
      undefined,
      this.buildRecordingEventDetails(fields)
    );
  }

  /** modifyRecording — emit once a scheduled single recording's options change. */
  public static logModifyRecording(fields: Partial<TAnalyticsFields>): void {
    this.trackEvent(
      EVENT_NAME_MODIFY_RECORDING,
      undefined,
      this.buildRecordingEventDetails(fields)
    );
  }

  /** cancelRecording — emit once a scheduled single recording is canceled before it airs/completes. */
  public static logCancelRecording(fields: Partial<TAnalyticsFields>): void {
    this.trackEvent(
      EVENT_NAME_CANCEL_RECORDING,
      undefined,
      this.buildRecordingEventDetails(fields)
    );
  }

  /** deleteRecording — emit once a completed recording is deleted (soft or permanent). */
  public static logDeleteRecording(fields: Partial<TAnalyticsFields>): void {
    this.trackEvent(
      EVENT_NAME_DELETE_RECORDING,
      undefined,
      this.buildRecordingEventDetails(fields)
    );
  }

  /** createOnePass — emit once a series recording/OnePass is created. */
  public static logCreateOnePass(fields: Partial<TAnalyticsFields>): void {
    this.trackEvent(
      EVENT_NAME_CREATE_ONE_PASS,
      undefined,
      this.buildRecordingEventDetails(fields)
    );
  }

  /** modifyOnePass — emit once an existing series recording/OnePass's options change. */
  public static logModifyOnePass(fields: Partial<TAnalyticsFields>): void {
    this.trackEvent(
      EVENT_NAME_MODIFY_ONE_PASS,
      undefined,
      this.buildRecordingEventDetails(fields)
    );
  }

  /** cancelOnePass — emit once a series recording/OnePass is canceled. */
  public static logCancelOnePass(fields: Partial<TAnalyticsFields>): void {
    this.trackEvent(
      EVENT_NAME_CANCEL_ONE_PASS,
      undefined,
      this.buildRecordingEventDetails(fields)
    );
  }
}
